import { OrganizationSettingsDTOCommissionRuleEnum } from '@reposit/api-client';
import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import Modal from '../../components/Modal';
import { Header3, P2 } from '../../components/Typography';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORGANIZATION_COMMISSION_RULE_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_COMMISSION_RULE_STORE_KEY,
  updateOrganizationCommissionRuleRequested,
} from '../../redux/organization/organization.actions';
import { EditableCommissionRule } from '../../redux/organization/organization.types';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';

export type CommissionRuleLabels = {
  [key in OrganizationSettingsDTOCommissionRuleEnum]: string;
};

interface EditCommissionRateModalProps {
  organizationId: string;
  commissionRule?: OrganizationSettingsDTOCommissionRuleEnum;
  commissionRuleLabels: CommissionRuleLabels;
  onDismiss: () => void;
}

interface EditCommissionRateFormValues {
  commissionRule: EditableCommissionRule | '';
}

const commissionRuleOptions: EditableCommissionRule[] = [
  OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE12POINT5,
  OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE15,
  OrganizationSettingsDTOCommissionRuleEnum.FEEPERCENTAGE20,
  OrganizationSettingsDTOCommissionRuleEnum.NOCOMMISSION,
];

const getInitialCommissionRule = (commissionRule?: OrganizationSettingsDTOCommissionRuleEnum) =>
  commissionRule && commissionRuleOptions.includes(commissionRule) ? commissionRule : '';

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const ManualEditUnavailableWarning = styled(P2)`
  background: ${(props) => props.theme.colors.warning};
  border-radius: 8px;
  color: #fff;
  margin: 0 0 16px;
  padding: 12px 16px;
`;

const EditCommissionRateModal: React.FC<EditCommissionRateModalProps> = ({
  organizationId,
  commissionRule,
  commissionRuleLabels,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_COMMISSION_RULE_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORGANIZATION_COMMISSION_RULE_MODAL_STORE_KEY]);
  const cannotEditCommissionRuleManually = !!commissionRule && !commissionRuleOptions.includes(commissionRule);
  const commissionRuleLabel = commissionRule ? commissionRuleLabels[commissionRule] : undefined;
  const manualEditUnavailableMessage = `This organisation currently has a commission rate that cannot be edited manually: ${commissionRuleLabel}.`;
  const validationSchema = Yup.object().shape({
    commissionRule: Yup.string().oneOf(commissionRuleOptions).required('Required'),
  });

  return (
    <Modal onDismiss={onDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 15 }}>Edit Commission Rate</Header3>
      {cannotEditCommissionRuleManually ? (
        <ManualEditUnavailableWarning>{manualEditUnavailableMessage}</ManualEditUnavailableWarning>
      ) : null}
      {cannotEditCommissionRuleManually ? (
        <Container fluid>
          <Actions>
            <Button buttonType="secondary" noArrow={true} onClick={onDismiss} type="button">
              Cancel
            </Button>
          </Actions>
        </Container>
      ) : (
        <Formik<EditCommissionRateFormValues>
          initialValues={{
            commissionRule: getInitialCommissionRule(commissionRule),
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (!values.commissionRule) {
              return;
            }

            dispatch(
              updateOrganizationCommissionRuleRequested({
                organizationId,
                commissionRule: values.commissionRule,
              }),
            );
          }}
        >
          {({ errors, handleBlur, handleSubmit, setFieldValue, touched, values }) => (
            <form onSubmit={handleSubmit}>
              <FieldWithLabel label="Commission Rate" error={errors.commissionRule} touched={touched.commissionRule}>
                <Select
                  name="commissionRule"
                  onChange={(value) => setFieldValue('commissionRule', value)}
                  onBlur={handleBlur}
                  value={values.commissionRule}
                >
                  <option disabled value={0}>
                    Select a commission rate
                  </option>
                  {commissionRuleOptions.map((option) => (
                    <option key={option} value={option}>
                      {commissionRuleLabels[option]}
                    </option>
                  ))}
                </Select>
              </FieldWithLabel>
              <Container fluid>
                <Actions>
                  <Button buttonType="secondary" noArrow={true} onClick={onDismiss} type="button">
                    Cancel
                  </Button>
                  <Button disabled={isSubmitting} type="submit">
                    Save
                  </Button>
                </Actions>
              </Container>
            </form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default EditCommissionRateModal;
